import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Icon,
} from '@mui/material';
import {
  Home as HomeIcon,
  Restaurant as RestaurantIcon,
  Article as ArticleIcon,
  MenuBook as MenuBookIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import HeroSection from './HeroSection';

export default function NotFoundPage() {
  const quickLinks = [
    { title: 'Home', icon: <HomeIcon />, path: '/', description: 'Back to homepage' },
    { title: 'Menu', icon: <MenuBookIcon />, path: '/menu', description: 'View our menu' },
    { title: 'Blog', icon: <ArticleIcon />, path: '/blog', description: 'Read our blog' },
    { title: 'Contact', icon: <PhoneIcon />, path: '/contact', description: 'Get in touch' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeroSection title="PAGE NOT FOUND" description="Oops! The page you're looking for doesn't exist." />

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          {/* 404 Large Text */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '80px', sm: '120px', md: '160px' },
              fontWeight: 900,
              background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            404
          </Typography>

          {/* Error Message */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
            }}
          >
            Sorry, This Page Is Lost in the Kitchen!
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
            }}
          >
            We couldn't find what you were looking for. It seems like you've wandered off the menu. 
            Let's get you back on track with some freshly cooked options below.
          </Typography>

          {/* Primary Action Button */}
          <Button
            variant="contained"
            size="large"
            href="/"
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: 2,
            }}
          >
            Go Back Home
          </Button>
        </Box>

        {/* Quick Links Section */}
        <Paper sx={{ p: 4, borderRadius: 2, bgcolor: '#f5f5f5', mb: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
            Quick Links - Get Back on Track
          </Typography>
          <Grid container spacing={3}>
            {quickLinks.map((link, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                      bgcolor: 'primary.main',
                      color: 'white',
                      '& svg': {
                        color: 'white',
                      },
                    },
                  }}
                  onClick={() => window.location.href = link.path}
                >
                  <CardContent>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: '50%',
                          bgcolor: 'primary.light',
                          color: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {link.icon}
                      </Box>
                    </Box>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {link.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {link.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* FAQ Section */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            Have Questions?
          </Typography>
          <Grid container spacing={2}>
            {[
              { q: 'How do I place an order?', a: 'Visit our menu page and add items to your cart.' },
              { q: 'What are your delivery times?', a: 'We deliver 24/7 with an average wait of 30-45 minutes.' },
              { q: 'Can I modify my order?', a: 'Contact us immediately at our support hotline.' },
              { q: 'Do you have a loyalty program?', a: 'Yes! Check out our blog for more details.' },
            ].map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {faq.q}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {faq.a}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
